import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle, Calculator, CheckCircle2, ChevronDown, ChevronRight, FileSearch, Loader2,
  Play, RotateCcw, ShieldCheck, StopCircle,
} from "lucide-react";

/** RE-READ EVERY BUILD SHEET — count first, his press, undo per truck.
 *
 *  The same shape as the VIN re-decode beside it, deliberately: count, read it over, then press.
 *  The count opens every sheet and writes nothing. An exact reader costs nothing, so its outcomes
 *  are the real thing, value by value. An AI sheet cannot be known without paying, so it carries an
 *  estimate and is OFF unless he ticks it. */

const n = (v) => (v === null || v === undefined ? 0 : v);

const JOB = {
  exact: { word: "EXACT", cls: "bg-emerald-100 text-emerald-800" },
  ai_cheap: { word: "AI · CHEAP", cls: "bg-sky-100 text-sky-800" },
  ai_full: { word: "AI · FULL READ", cls: "bg-amber-100 text-amber-900" },
  unreadable: { word: "CANNOT OPEN", cls: "bg-slate-200 text-slate-600" },
};

export function SheetRereadPanel({ canManage = false, testid = "sheet-reread" }) {
  const [cohort, setCohort] = useState(null);
  const [count, setCount] = useState(null);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);
  const [openRun, setOpenRun] = useState(true);
  const [ask, setAsk] = useState(false);
  const [pick, setPick] = useState({ exact: true, ai_cheap: false, ai_full: false });
  const [undoing, setUndoing] = useState("");
  const pollC = useRef(null);
  const pollR = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, l, r] = await Promise.all([
        api.get("/catch-up/sheet-reread/cohort"),
        api.get("/catch-up/sheet-reread/count/last"),
        api.get("/catch-up/sheet-reread/run/last"),
      ]);
      setCohort(c.data);
      setCount(l.data.run);
      setRun(r.data.run);
      if (l.data.run?.status === "running" && !pollC.current) {
        pollC.current = setInterval(async () => {
          const { data } = await api.get("/catch-up/sheet-reread/count/last");
          setCount(data.run);
          if (data.run?.status !== "running") { clearInterval(pollC.current); pollC.current = null; }
        }, 3000);
      }
      if (r.data.run?.status === "running" && !pollR.current) {
        pollR.current = setInterval(async () => {
          const { data } = await api.get("/catch-up/sheet-reread/run/last");
          setRun(data.run);
          if (data.run?.status !== "running") { clearInterval(pollR.current); pollR.current = null; }
        }, 3000);
      }
    } catch { /* the panel simply shows nothing rather than a half-truth */ }
  }, []);

  useEffect(() => {
    load();
    return () => {
      if (pollC.current) clearInterval(pollC.current);
      if (pollR.current) clearInterval(pollR.current);
    };
  }, [load]);

  const startCount = async () => {
    setBusy("count");
    try {
      await api.post("/catch-up/sheet-reread/count");
      toast.success("Counting — it opens every sheet and writes nothing.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "The count could not start.");
    } finally { setBusy(""); }
  };

  const startRun = async () => {
    setAsk(false);
    setBusy("run");
    try {
      await api.post("/catch-up/sheet-reread/run", {
        confirm: true, include_exact: pick.exact, include_ai_cheap: pick.ai_cheap,
        include_ai_full: pick.ai_full, from_count: count?.id,
      });
      toast.success("Running in the background. Undo sits beside every truck.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "The run could not start.");
    } finally { setBusy(""); }
  };

  const stop = async (which, id) => {
    try {
      await api.post(`/catch-up/sheet-reread/${which}/${id}/stop`);
      toast.info("Stopping — everything already done stays done.");
      await load();
    } catch { toast.error("It could not be stopped."); }
  };

  const undo = async (t) => {
    setUndoing(t.donor_id);
    try {
      const { data } = await api.post(`/catch-up/sheet-reread/run/${run.id}/undo/${t.donor_id}`);
      toast.success(data.say || "Put back.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "It could not be put back.");
    } finally { setUndoing(""); }
  };

  const c = count?.counts || {};
  const cost = count?.cost || {};
  const sheets = count?.sheets || [];
  const chosen = sheets.filter((s) => pick[s.job]);
  const chosenCost = chosen.reduce((a, s) => a + (s.cost_usd || 0), 0);
  const jobCount = (j) => sheets.filter((s) => s.job === j).length;
  const jobCost = (j) => sheets.filter((s) => s.job === j).reduce((a, s) => a + (s.cost_usd || 0), 0);

  return (
    <section data-testid={testid} className="rounded-xl border-2 border-[#1F1F1F] bg-white overflow-hidden">
      <div className="bg-[#1F1F1F] text-white px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileSearch className="w-5 h-5 shrink-0" />
          <h2 className="font-display text-lg font-bold truncate">Re-read every build sheet</h2>
        </div>
        <span data-testid={`${testid}-state`}
          className="text-[10px] font-mono uppercase tracking-widest bg-blue-600 px-2 py-1 rounded shrink-0">
          Count only
        </span>
      </div>

      <div className="p-4 space-y-4">
        <p className="text-sm text-slate-700">
          Every build sheet already on a truck, read again with the readers as they are today.
          <b> The count opens each one and writes nothing</b> — it names the reader each sheet would go
          through and what it would do. An exact reader costs nothing. An AI sheet carries a price, and
          stays off unless you tick it.
        </p>

        {cohort && (
          <p className="text-sm text-slate-600" data-testid={`${testid}-cohort`}>{cohort.say}</p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid={`${testid}-count`} onClick={startCount}
            disabled={!canManage || busy === "count" || count?.status === "running" || !cohort?.countable}
            className="rounded-lg h-12 px-4 bg-[#1F1F1F] hover:bg-black font-bold">
            {busy === "count" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Calculator className="w-4 h-4 mr-1.5" />}
            Count first — writes nothing
          </Button>
          {count?.status === "running" && (
            <Button data-testid={`${testid}-count-stop`} variant="outline"
              onClick={() => stop("count", count.id)} className="rounded-lg h-12 px-4 border-slate-300">
              <StopCircle className="w-4 h-4 mr-1.5" />Stop counting
            </Button>
          )}
        </div>

        {count?.status === "running" && (
          <div data-testid={`${testid}-count-progress`} className="rounded-lg border-2 border-blue-200 bg-blue-50 p-3 flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-blue-700 shrink-0" />
            <span className="text-sm text-slate-800">
              Opened {n(count.scanned)} of {n(count.total)} sheet(s). Nothing is being written.
            </span>
          </div>
        )}

        {count?.status === "failed" && (
          <div data-testid={`${testid}-count-failed`} className="rounded-lg border-2 border-red-300 bg-red-50 p-3 text-sm">
            <b>The count stopped:</b> {count.error}. No truck was touched.
          </div>
        )}

        {count && count.status !== "running" && (count.sheets_total || 0) > 0 && (
          <div data-testid={`${testid}-report`} className="space-y-3">
            <div data-testid={`${testid}-wrote-nothing`} className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
              <span className="text-sm text-slate-800">
                <b>Nothing was written.</b> {n(count.sheets_total)} sheet(s) opened and read through,
                counted as at {(count.finished_at || "").slice(0, 16).replace("T", " ")}.
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[["would fill", c.values_that_would_fill, "green"],
                ["would refresh", c.values_that_would_refresh, "slate"],
                ["put beside", c.values_put_beside, "amber"],
                ["left alone", c.left_alone, "slate"]].map(([label, big, tone]) => (
                <div key={label} data-testid={`${testid}-stat-${label.replace(/ /g, "-")}`}
                  className={`rounded-xl border-2 p-3 ${tone === "green" ? "border-emerald-200 bg-emerald-50"
                    : tone === "amber" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
                  <div className="font-display text-3xl font-black leading-none">{n(big)}</div>
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mt-1.5">{label}</div>
                </div>
              ))}
            </div>

            <div data-testid={`${testid}-by-reader`} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">
                Which reader each sheet would go through
              </div>
              {Object.entries(count.by_reader || {}).map(([label, num]) => (
                <div key={label} className="flex justify-between text-sm py-0.5">
                  <span className="text-slate-700">{label}</span>
                  <b>{num}</b>
                </div>
              ))}
            </div>

            {/* ------------------------------------------------ what to run, and what it costs */}
            <div data-testid={`${testid}-picker`} className="rounded-lg border-2 border-[#1F1F1F] bg-white p-3 space-y-2">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                What to run — and what it costs
              </div>
              {[["exact", "Exact readers — character for character", "free, and the outcomes above are the real thing"],
                ["ai_cheap", "AI re-read of sheets already transcribed", "the cheap text job"],
                ["ai_full", "AI full read — the picture read from scratch", "the dearest job"]].map(([k, label, why]) => (
                <label key={k} data-testid={`${testid}-pick-${k}`}
                  className={`flex items-start gap-3 p-2 rounded-lg ${jobCount(k) ? "hover:bg-slate-50 cursor-pointer" : "opacity-50"}`}>
                  <input type="checkbox" className="mt-1 w-5 h-5 accent-[#B01E28]"
                    data-testid={`${testid}-tick-${k}`} disabled={!jobCount(k) || !canManage}
                    checked={Boolean(pick[k] && jobCount(k))}
                    onChange={(e) => setPick((p) => ({ ...p, [k]: e.target.checked }))} />
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-[#1F1F1F]">{label}</div>
                    <div className="text-[12px] text-slate-600">
                      {jobCount(k)} sheet(s) · {jobCost(k) > 0 ? `about $${jobCost(k).toFixed(4)}` : "no cost"} · {why}
                    </div>
                  </div>
                </label>
              ))}
              <p className="text-[11px] text-slate-500" data-testid={`${testid}-cost-basis`}>
                <b>An estimate, not a bill</b> — {cost.basis}
              </p>
            </div>

            <button data-testid={`${testid}-toggle-sheets`} onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 text-sm font-bold">
              <span>Every sheet, one by one ({n(count.sheets_total)})</span>
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {open && (
              <div data-testid={`${testid}-sheet-list`} className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {sheets.map((s) => (
                  <div key={s.donor_id} data-testid={`${testid}-sheet-${s.donor_id}`} className="p-3 text-[12px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`font-mono text-[10px] px-1.5 py-0.5 ${JOB[s.job]?.cls}`}>{JOB[s.job]?.word}</span>
                      <b className="text-slate-800">{s.stock_number}</b>
                      <span className="text-slate-500">{s.title}</span>
                      <span className="text-slate-400">· {s.file}</span>
                    </div>
                    <div className="text-slate-600 mt-0.5">
                      {s.reader || "no reader"} · {s.cost_usd ? `about $${s.cost_usd}` : "no cost"}
                      {s.counts && ` · fill ${s.counts.fill}, refresh ${s.counts.refresh}, beside ${s.counts.beside}, left alone ${s.counts.protected}`}
                      {s.most_it_could_fill !== undefined && ` · at most ${s.most_it_could_fill} empty box(es) could fill`}
                    </div>
                    <div className="text-slate-500 mt-0.5">{s.why}</div>
                    {!!(s.disagreements || []).length && (
                      <div className="text-amber-900 mt-1">
                        <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                        {s.disagreements[0].say}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <Button data-testid={`${testid}-go`} onClick={() => setAsk(true)}
              disabled={!canManage || !chosen.length || run?.status === "running"}
              className="rounded-lg h-12 px-4 bg-[#B01E28] hover:bg-[#8f1820] font-bold w-full sm:w-auto">
              <Play className="w-4 h-4 mr-1.5" />
              Re-read {chosen.length} sheet(s){chosenCost > 0 ? ` — about $${chosenCost.toFixed(4)}` : " — no cost"}
            </Button>
          </div>
        )}

        {/* ---------------------------------------------------------------- THE RUN */}
        {run && (
          <div data-testid={`${testid}-run`} className="rounded-lg border-2 border-[#B01E28] bg-white overflow-hidden">
            <div className="bg-[#B01E28] text-white px-3 py-2 flex items-center justify-between gap-2">
              <b className="text-sm">The run that wrote</b>
              <span data-testid={`${testid}-run-state`} className="text-[10px] font-mono uppercase tracking-widest">
                {run.status}
              </span>
            </div>
            <div className="p-3 space-y-2 text-sm">
              {run.status === "running" && (
                <div data-testid={`${testid}-run-progress`} className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#B01E28]" />
                  <span>{n(run.scanned)} of {n(run.total)} sheet(s) · spent so far ${n(run.spent_usd)}</span>
                  <Button size="sm" variant="outline" className="ml-auto h-8"
                    data-testid={`${testid}-run-stop`} onClick={() => stop("run", run.id)}>
                    <StopCircle className="w-3.5 h-3.5 mr-1" />Stop
                  </Button>
                </div>
              )}
              <p data-testid={`${testid}-run-summary`} className="text-slate-700">
                <b>{n(run.counts?.values_filled)}</b> filled, <b>{n(run.counts?.values_refreshed)}</b> refreshed,
                <b> {n(run.counts?.values_put_beside)}</b> put beside, <b>{n(run.counts?.option_codes)}</b> option
                code(s) and <b>{n(run.counts?.components)}</b> component(s) across <b>{n(run.counts?.trucks_changed)}</b> truck(s).
                {n(run.counts?.ai_values_drafted) > 0 && <> <b>{n(run.counts.ai_values_drafted)}</b> AI value(s) drafted, each still needing your ✓.</>}
                {" "}Actually spent: <b>${n(run.spent_usd)}</b>{run.estimate?.total_usd ? ` (estimated $${run.estimate.total_usd})` : ""}.
                {n(run.counts?.failed) > 0 && <> <b className="text-[#B01E28]">{n(run.counts.failed)} could not be read</b> — listed below.</>}
              </p>
              <p className="text-[11px] text-slate-500" data-testid={`${testid}-run-stamp`}>
                {run.included?.join(", ")} · started {(run.started_at || "").slice(0, 16).replace("T", " ")} by {run.started_by}
              </p>

              <button data-testid={`${testid}-toggle-run-trucks`} onClick={() => setOpenRun((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 text-sm font-bold">
                <span>The trucks it wrote to ({n(run.trucks_total)})</span>
                {openRun ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {openRun && (
                <div data-testid={`${testid}-run-truck-list`} className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {(run.trucks || []).map((t) => (
                    <div key={t.donor_id} data-testid={`${testid}-run-truck-${t.donor_id}`}
                      className="p-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
                      <b className="text-slate-800">{t.stock_number}</b>
                      <span className="text-slate-500">{t.title}</span>
                      {t.reader && <span className="text-slate-400">· {t.reader}</span>}
                      {t.wrote && (
                        <span className="text-slate-600">
                          · filled {t.wrote.filled}, refreshed {t.wrote.refreshed}, beside {t.wrote.beside}
                        </span>
                      )}
                      {t.ai_drafted !== undefined && (
                        <span className="text-sky-800">· {t.ai_drafted} drafted, needs your ✓</span>
                      )}
                      {t.note && <span className="text-amber-900 basis-full">{t.note}</span>}
                      {t.undone
                        ? <span data-testid={`${testid}-undone-${t.donor_id}`} className="ml-auto text-[10px] font-mono uppercase bg-slate-200 px-1.5 py-0.5">put back</span>
                        : canManage && (t.wrote || t.ai_drafted !== undefined) && (
                          <Button size="sm" variant="ghost" className="h-7 text-[11px] ml-auto"
                            data-testid={`${testid}-undo-${t.donor_id}`} disabled={undoing === t.donor_id}
                            onClick={() => undo(t)}>
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />Undo this truck
                          </Button>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---------------------------------------------------------------- THE CONFIRM */}
      <Dialog open={ask} onOpenChange={setAsk}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid={`${testid}-confirm`}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-[#B01E28]">
              <Play className="w-5 h-5" />Re-read {chosen.length} sheet(s)?
            </DialogTitle>
            <DialogDescription>
              This one writes. It will fill <b>{n(c.values_that_would_fill)}</b> empty box(es), refresh
              <b> {n(c.values_that_would_refresh)}</b> machine value(s) and put <b>{n(c.values_put_beside)}</b> note(s)
              beside values with no record of who typed them. Anything a person entered, corrected or
              checked is not touched. {chosenCost > 0
                ? <>About <b>${chosenCost.toFixed(4)}</b> of AI — an estimate, not a bill.</>
                : <>No AI, so <b>no cost at all</b>.</>} Undo sits beside every truck.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg h-11" data-testid={`${testid}-cancel`}
              onClick={() => setAsk(false)}>Not yet</Button>
            <Button data-testid={`${testid}-confirm-go`} onClick={startRun}
              className="rounded-lg h-11 bg-[#B01E28] hover:bg-[#8f1820] font-bold">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />Yes — run it now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default SheetRereadPanel;
